import React from "react";
import { FieldError } from "react-hook-form";
import { translate } from "@kaltura/mediaspace-shared-utils";
import { uniqueId } from "lodash";
import SmartContainer from "./smart-container/SmartContainer";
import { FormFieldError, StyledInput, TextFieldLoading } from "@kaltura/mediaspace-shared-ui/form";
import { useUtilityClasses } from "./fieldClasses";
import SmartFormLabel from "./smart-form-label/SmartFormLabel";
import { HtmlReactParser } from "@kaltura/mediaspace-shared-utils";
import styled from "@emotion/styled";

export interface Props {
    className?: string;
    title: string;
    name: string;
    id?: string;
    readOnly?: boolean;
    placeholder?: string;
    defaultValue?: string;
    value?: string;
    error?: FieldError;
    errorMessage?: string;
    required?: boolean;
    type?: string;
    onChange?: React.ChangeEventHandler<HTMLInputElement>;
    onBlur?: React.FocusEventHandler<HTMLInputElement>;
    isLoading?: boolean;
    autocomplete?: string;
}

const StyledInputWrapper = styled("span")(({ theme }) => ({
    position: "relative",
}));

/**
 * text input field for site registration form
 */
const TextField = React.forwardRef<any, Props>((props: Props, ref) => {
    const {
        className,
        title,
        name,
        id = uniqueId("input_"),
        readOnly = false,
        placeholder,
        defaultValue,
        value,
        error,
        errorMessage,
        required = false,
        onChange,
        onBlur,
        isLoading,
        autocomplete = "off",
        type = "text",
    } = props;

    const classes = useUtilityClasses();

    return (
        <SmartContainer className={`${classes.root} ${className}`}>
            <SmartFormLabel htmlFor={id} className={classes.label} required={required} text={title} />
            <StyledInputWrapper className={classes.input}>
                <StyledInput
                    id={id}
                    error={!!error}
                    inputRef={ref}
                    type={type}
                    name={name}
                    aria-invalid={error ? "true" : "false"}
                    readOnly={readOnly}
                    placeholder={placeholder}
                    defaultValue={defaultValue}
                    value={value}
                    autoComplete={autocomplete}
                    onChange={onChange}
                    onBlur={onBlur}
                    required={required}
                    endAdornment={isLoading && <TextFieldLoading />}
                />
                <div aria-live="polite">
                    {error && errorMessage && <FormFieldError>{HtmlReactParser(errorMessage)}</FormFieldError>}
                    {error && !errorMessage && <FormFieldError>{translate("%1 is required", [title])}</FormFieldError>}
                </div>
            </StyledInputWrapper>
        </SmartContainer>
    );
});

export default TextField;
